/**
 * SiteFooter — Redesigned premium footer.
 * Uses centralized navigation config and includes newsletter signup.
 */

import { Link } from "@tanstack/react-router";
import { Instagram } from "lucide-react";
import { siteConfig } from "@/config/site";
import { footerNav } from "@/config/navigation";
import logoImg from "@/assets/logo.webp";

export function SiteFooter() {
  return (
    <footer className="border-t bg-secondary/30">
      {/* Main footer content */}
      <div className="section-container grid grid-cols-2 gap-8 py-12 sm:grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_1fr] md:py-20">
        {/* Brand column */}
        <div className="col-span-2 max-w-sm md:col-span-1">
          <Link to="/" className="inline-block">
            <img
              src={logoImg}
              alt="Aster Decorz"
              width={260}
              height={87}
              loading="lazy"
              decoding="async"
              className="h-14 sm:h-16 w-auto max-w-[260px] object-contain filter contrast-[1.65] saturate-[1.4] brightness-[0.88]"
            />
          </Link>
          <p className="mt-1 text-xs tracking-[0.12em] uppercase text-muted-foreground">
            {siteConfig.subtitle}
          </p>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            {siteConfig.description}
          </p>
          {/* Social links */}
          <div className="mt-6 flex items-center gap-4">
            <a
              href={siteConfig.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow on Instagram"
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              <Instagram className="h-5 w-5" strokeWidth={1.25} />
            </a>
            <a
              href={siteConfig.social.pinterest}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow on Pinterest"
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M8 12a4 4 0 1 1 8 0c0 3-2 6-4 8" />
                <path d="M12 12v-8" />
              </svg>
            </a>
            <a
              href="https://wa.me/919344580982?text=Hello%20Aster%20Decorz!%20%F0%9F%91%8B%20I%20have%20an%20inquiry%20about%20your%20products%20or%20orders."
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat with Aster Decorz on WhatsApp"
              className="text-muted-foreground transition-colors hover:text-[#25D366]"
            >
              <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2ZM12.05 20.16C10.57 20.16 9.12 19.76 7.85 19.01L7.55 18.83L4.43 19.65L5.26 16.61L5.06 16.29C4.24 14.99 3.8 13.47 3.8 11.91C3.8 7.37 7.5 3.67 12.05 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.16 12.05 20.16ZM16.57 14.39C16.32 14.26 15.1 13.66 14.87 13.58C14.65 13.5 14.49 13.46 14.32 13.71C14.16 13.96 13.7 14.49 13.55 14.66C13.41 14.82 13.26 14.85 13.01 14.72C12.77 14.6 11.98 14.34 11.05 13.51C10.33 12.86 9.84 12.06 9.69 11.82C9.55 11.57 9.68 11.43 9.8 11.31C9.91 11.2 10.05 11.02 10.17 10.88C10.29 10.74 10.33 10.63 10.41 10.47C10.49 10.31 10.45 10.17 10.39 10.05C10.33 9.93 9.84 8.73 9.63 8.23C9.43 7.75 9.23 7.81 9.08 7.81C8.93 7.8 8.77 7.8 8.61 7.8C8.44 7.8 8.17 7.86 7.94 8.11C7.72 8.36 7.08 8.96 7.08 10.18C7.08 11.4 7.97 12.57 8.09 12.74C8.21 12.9 9.83 15.4 12.32 16.47C12.91 16.73 13.37 16.88 13.73 16.99C14.32 17.18 14.86 17.15 15.29 17.09C15.77 17.02 16.76 16.49 16.97 15.91C17.17 15.34 17.17 14.85 17.11 14.75C17.05 14.65 16.82 14.51 16.57 14.39Z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Navigation columns */}
        {footerNav.map((column) => (
          <div key={column.title}>
            <p className="eyebrow">{column.title}</p>
            <ul className="mt-5 space-y-3 text-sm">
              {column.items.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    className="rule-link text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Bottom bar */}
      <div className="section-container flex flex-col gap-3 border-t px-5 py-6 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between md:px-10">
        <p>
          © {new Date().getFullYear()} {siteConfig.name}. All rights reserved.
        </p>
        <div className="flex items-center gap-6">
          <Link to="/privacy" className="transition-colors hover:text-foreground">
            Privacy Policy
          </Link>
          <Link to="/terms" className="transition-colors hover:text-foreground">
            Terms of Service
          </Link>
        </div>
      </div>
    </footer>
  );
}
